// src/components/visuals/ScrollCue.jsx
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import "../assets/styles/scrollCue.css";
import { useTranslation } from "../hooks/useTranslation";

export default function ScrollCue({
    labelKey = "home.hero.scroll",
    position = "right",              // "right" | "center"
    theme = "light",                 // "light" | "dark"
    cueOffset = "clamp(12px, 4vh, 40px)",
    dotSize = 6,                     // px
    mouseWidth = 22,                 // px
    mouseHeight = 36,                // px
    lineBelow = 56,                  // px (length of dashed line under the mouse)
    mouseBase = 0,                   // px start offset *below the dot*
    mouseShift = 16,                 // px animated travel distance
    duration = 1.4,                  // s
    className = ""
}) {
    // render safely while i18n loads
    const t = useTranslation();

    // Reduced motion
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const on = (e) => setReduced(e.matches);
        on(mq); mq.addEventListener?.("change", on);
        return () => mq.removeEventListener?.("change", on);
    }, []);

    // Safe label (empty until ready)
    const label = t("home.hero.scrollcue");

    return (
        <div
            className={[
                "scrollCue",
                `scrollCue--${position}`,
                `scrollCue--${theme}`,
                reduced ? "is-reduced" : "",
                className
            ].join(" ")}
            style={{
                "--cue-offset": cueOffset,
                "--dot-size": `${dotSize}px`,
                "--mouse-w": `${mouseWidth}px`,
                "--mouse-h": `${mouseHeight}px`,
                "--line-below": `${lineBelow}px`,
                "--mouse-base": `${mouseBase}px`,
                "--mouse-shift": `${mouseShift}px`,
                "--mouse-duration": `${duration}s`
            }}
            aria-hidden={!label}                 // hide from a11y if we have no text yet
            aria-label={label || undefined}      // expose label once ready
        >
            {label && <span className="scrollCue__label">{label}</span>}

            {/* Track = dot + mouse + lineBelow */}
            <div className="scrollCue__track">
                <span className="scrollCue__dotFixed" />
                <span className="scrollCue__line" />
                <div
                    className="scrollCue__mouse"
                    style={reduced ? { animation: "none" } : undefined}
                >
                    <span className="scrollCue__wheel" />
                </div>
            </div>
        </div>
    );
}

ScrollCue.propTypes = {
    labelKey: PropTypes.string,
    position: PropTypes.oneOf(["right", "center"]),
    theme: PropTypes.oneOf(["light", "dark"]),
    cueOffset: PropTypes.string,
    dotSize: PropTypes.number,
    mouseWidth: PropTypes.number,
    mouseHeight: PropTypes.number,
    lineBelow: PropTypes.number,
    mouseBase: PropTypes.number,
    mouseShift: PropTypes.number,
    duration: PropTypes.number,
    className: PropTypes.string
};
